import { useEffect, useState, useMemo } from "react";
import { Clock, Calendar, CurrencyEur, TrendUp, TrendDown, Printer, Warning, WarningCircle, MapPin, PencilSimple, Trash, Plus, X, Check, Coffee, FileArrowDown, CaretDown, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import AdvancesDialog from "../components/AdvancesDialog";
import { SortHeader } from "../components/SortHeader";
import { useTableSort } from "../lib/useTableSort";
import { useRoles, roleLabel } from "../lib/roles";

const PERIODS = [
  { value: "day", label: "Día" },
  { value: "week", label: "Semana" },
  { value: "month", label: "Mes" },
  { value: "custom", label: "Rango" },
];

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function weekAgoISO() {
  const d = new Date();
  d.setDate(d.getDate() - 6);
  return d.toISOString().slice(0, 10);
}

function formatDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// CSV helpers — Excel-friendly (BOM UTF-8, ';' separator, CRLF).
function csvEscape(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (/[";\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}
function csvJoin(rows) {
  return rows.map((r) => r.map(csvEscape).join(";")).join("\r\n");
}
function downloadCsv(filename, csvText) {
  const bom = "\uFEFF";
  const blob = new Blob([bom + csvText], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
function safeFileSegment(s) {
  return String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^\w-]+/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, "");
}
function csvDateRangeLabel(period, summary, date, dateFrom, dateTo) {
  if (period === "custom") return `${dateFrom} a ${dateTo}`;
  if (summary?.range_start && summary?.range_end) {
    const s = summary.range_start.slice(0, 10);
    const eIso = new Date(new Date(summary.range_end).getTime() - 86400000).toISOString();
    const e = eIso.slice(0, 10);
    return s === e ? s : `${s} a ${e}`;
  }
  return date || "";
}

export default function AdminDashboard() {
  const [workers, setWorkers] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [period, setPeriod] = useState("week");
  const [date, setDate] = useState(todayISO());
  const [dateFrom, setDateFrom] = useState(weekAgoISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editPunch, setEditPunch] = useState(null); // null | {} for new | punch for edit
  const [savingPunch, setSavingPunch] = useState(false);
  const [showModifications, setShowModifications] = useState(true);
  const [showInfoBreaks, setShowInfoBreaks] = useState(false); // "informativos" (non-discounting) hidden by default
  const [showRawTotals, setShowRawTotals] = useState(false);
  const [hidePunchActions, setHidePunchActions] = useState(false);
  const [pendingAdvances, setPendingAdvances] = useState(null);
  const [advancesPanelOpen, setAdvancesPanelOpen] = useState(false);
  const [advancesWorker, setAdvancesWorker] = useState(null);

  const loadPendingAdvances = async () => {
    try {
      const { data } = await api.get("/advances/pending-summary");
      setPendingAdvances(data);
    } catch {
      setPendingAdvances(null);
    }
  };

  useEffect(() => {
    loadPendingAdvances();
  }, []);

  const [roles] = useRoles();
  const isGroup = typeof selectedId === "string" && selectedId.startsWith("__group_");
  const groupRole = isGroup ? selectedId.slice(8, -2) : null;
  const groupLabel = groupRole ? `${roleLabel(roles, groupRole)} (todos)` : "";
  const isBreak = typeof selectedId === "string" && selectedId.startsWith("__break_");
  const breakReason = isBreak ? selectedId.replace("__break_", "") : null;
  const [breakReasons, setBreakReasons] = useState([]);
  const [breakFamilies, setBreakFamilies] = useState([]);
  const [familyFilter, setFamilyFilter] = useState(""); // "" = todas
  const advSort = useTableSort(pendingAdvances?.breakdown || [], "name", "asc");
  const breakSumSort = useTableSort(summary?.rows || [], "start_time", "desc");
  const groupSort = useTableSort(summary?.breakdown || [], "user.name", "asc");
  const breaksSort = useTableSort(summary?.breaks || [], "start_time", "desc");
  const mergedRows = useMemo(
    () => (summary ? mergeRows(summary, showModifications, showInfoBreaks, familyFilter) : []),
    [summary, showModifications, showInfoBreaks, familyFilter],
  );
  const punchSort = useTableSort(mergedRows, "ts", "desc");

  useEffect(() => {
    api.get("/settings/public/break-reasons")
      .then((r) => {
        setBreakReasons((r.data.reasons || []).map((x) => typeof x === "string" ? x : x?.name).filter(Boolean));
        setBreakFamilies(r.data.families || []);
      })
      .catch(() => { setBreakReasons([]); setBreakFamilies([]); });
  }, []);

  const reloadSummary = async () => {
    if (!selectedId) return;
    try {
      const commonParams = period === "custom"
        ? { period, date_from: dateFrom, date_to: dateTo }
        : { period, date };
      if (isBreak) {
        const { data } = await api.get("/reports/breaks-summary", { params: { reason: breakReason || "all", ...commonParams } });
        setSummary(data);
      } else if (isGroup) {
        const { data } = await api.get("/reports/group-summary", { params: { role: groupRole, ...commonParams } });
        setSummary(data);
      } else {
        const { data } = await api.get("/reports/summary", { params: { user_id: selectedId, ...commonParams } });
        setSummary(data);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const savePunch = async (e) => {
    e.preventDefault();
    setSavingPunch(true);
    try {
      const payload = {
        user_id: editPunch.user_id || selectedId,
        start_time: editPunch.start_time,
        end_time: editPunch.end_time || null,
      };
      if (editPunch.id) {
        // Send excluded flag only on edit (not on create)
        payload.excluded = !!editPunch.excluded;
        await api.put(`/punch/${editPunch.id}`, payload);
        toast.success("Fichaje actualizado");
      } else {
        await api.post("/punch", payload);
        toast.success("Fichaje creado");
      }
      setEditPunch(null);
      reloadSummary();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setSavingPunch(false); }
  };

  const deletePunch = async (p) => {
    if (!window.confirm("¿Eliminar este fichaje?")) return;
    try {
      await api.delete(`/punch/${p.id}`);
      toast.success("Eliminado");
      reloadSummary();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const forceClose = async (p) => {
    if (!window.confirm("¿Cerrar este fichaje ahora?")) return;
    try {
      await api.put(`/punch/${p.id}`, { end_time: new Date().toISOString() });
      toast.success("Cerrado");
      reloadSummary();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const approveIncidence = async (inc) => {
    try {
      await api.post(`/incidences/${inc.id}/approve`, { exclude_overlaps: true });
      toast.success("Incidencia aprobada. Los fichajes solapados se marcan en gris y no computan.");
      reloadSummary();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const denyIncidence = async (inc) => {
    const motivo = window.prompt("Motivo del rechazo (opcional):", "");
    if (motivo === null) return; // cancelled
    try {
      await api.post(`/incidences/${inc.id}/deny`, { motivo_rechazo: motivo.trim() });
      toast.success("Incidencia denegada");
      reloadSummary();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteIncidence = async (inc) => {
    if (!window.confirm("¿Eliminar esta incidencia del historial?")) return;
    try {
      await api.delete(`/incidences/${inc.id}`);
      toast.success("Eliminada");
      reloadSummary();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toLocalInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users");
        setWorkers(data);
        if (data.length > 0) setSelectedId(data[0].id);
      } catch (e) {
        toast.error(formatApiError(e));
      }
      try {
        const { data: s } = await api.get("/settings");
        setHidePunchActions(!!s.hide_edit_delete_punch);
      } catch (err) { console.debug("settings fetch failed", err); }
    })();
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    (async () => {
      setLoading(true);
      try {
        const commonParams =
          period === "custom"
            ? { period, date_from: dateFrom, date_to: dateTo }
            : { period, date };
        if (isBreak) {
          const { data } = await api.get("/reports/breaks-summary", { params: { reason: breakReason || "all", ...commonParams } });
          setSummary(data);
        } else if (isGroup) {
          const { data } = await api.get("/reports/group-summary", { params: { role: groupRole, ...commonParams } });
          setSummary(data);
        } else {
          const { data } = await api.get("/reports/summary", { params: { user_id: selectedId, ...commonParams } });
          setSummary(data);
        }
      } catch (e) {
        toast.error(formatApiError(e));
        setSummary(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedId, period, date, dateFrom, dateTo]);

  const isPositive = (summary?.difference_hours ?? 0) >= 0;
  void isPositive;

  const exportCsv = () => {
    if (!summary) { toast.error("No hay datos para exportar"); return; }
    const rangeLabel = csvDateRangeLabel(period, summary, date, dateFrom, dateTo);
    const now = new Date().toLocaleString("es-ES");
    let subjectLabel = "";
    let rows = [];

    if (isBreak) {
      subjectLabel = `Tareas - ${breakReason || "todas"}`;
      rows.push(["Informe", "Tareas"]);
      rows.push(["Motivo", breakReason || "Todos"]);
      rows.push(["Periodo", period]);
      rows.push(["Rango", rangeLabel]);
      rows.push(["Generado", now]);
      rows.push([]);
      rows.push(["Total horas", (summary.total_hours ?? 0).toFixed(2)]);
      rows.push(["Nº tareas", summary.total_count ?? 0]);
      rows.push([]);
      rows.push(["Por trabajador"]);
      rows.push(["Trabajador", "Horas", "Nº tareas"]);
      (summary.by_user || []).forEach((u) => rows.push([u.user_name || "", (u.hours ?? 0).toFixed(2), u.count ?? 0]));
      rows.push([]);
      rows.push(["Por motivo"]);
      rows.push(["Motivo", "Horas", "Nº"]);
      (summary.by_reason || []).forEach((r) => rows.push([r.reason || "", (r.hours ?? 0).toFixed(2), r.count ?? 0]));
      rows.push([]);
      rows.push(["Detalle"]);
      rows.push(["Fecha inicio", "Fecha fin", "Trabajador", "Motivo", "Detalle", "Horas", "Estado"]);
      (summary.rows || []).forEach((b) => rows.push([
        b.start_time ? new Date(b.start_time).toLocaleString("es-ES") : "",
        b.end_time ? new Date(b.end_time).toLocaleString("es-ES") : "EN CURSO",
        b.user_name || "",
        b.reason || "",
        b.custom_reason || "",
        (b.duration_hours ?? 0).toFixed(2),
        b.is_open ? "abierto" : "cerrado",
      ]));
    } else if (isGroup) {
      subjectLabel = groupLabel;
      rows.push(["Informe de grupo", groupLabel]);
      rows.push(["Periodo", period]);
      rows.push(["Rango", rangeLabel]);
      rows.push(["Generado", now]);
      rows.push([]);
      rows.push(["Totales"]);
      rows.push(["Horas trabajadas", (summary.worked_hours ?? 0).toFixed(2)]);
      rows.push(["Horas contrato", (summary.contract_hours ?? 0).toFixed(2)]);
      rows.push(["Diferencia", (summary.difference_hours ?? 0).toFixed(2)]);
      rows.push(["Total a pagar (€)", (summary.total_pay ?? 0).toFixed(2)]);
      rows.push([]);
      rows.push(["Desglose por trabajador"]);
      rows.push(["Trabajador", "Rol", "Horas trabajadas", "Horas contrato", "Diferencia", "€/h", "Total (€)", "Mes: Horas", "Mes: Contrato", "Mes: Diferencia", "Mes: Total (€)"]);
      (summary.breakdown || []).forEach((b) => rows.push([
        b.user?.name || "",
        b.user?.role || "",
        (b.worked_hours ?? 0).toFixed(2),
        (b.contract_hours ?? 0).toFixed(2),
        (b.difference_hours ?? 0).toFixed(2),
        (b.hourly_rate ?? 0).toFixed(2),
        (b.total_pay ?? 0).toFixed(2),
        (b.month_worked_hours ?? 0).toFixed(2),
        (b.month_contract_hours ?? 0).toFixed(2),
        (b.month_difference_hours ?? 0).toFixed(2),
        (b.month_total_pay ?? 0).toFixed(2),
      ]));
    } else {
      const worked = showRawTotals ? (summary.worked_hours_raw ?? summary.worked_hours) : summary.worked_hours;
      const diff = worked - summary.contract_hours;
      const pay = diff * summary.hourly_rate;
      const workerName = summary.user?.name || "";
      subjectLabel = workerName;
      rows.push(["Informe de horas trabajadas"]);
      rows.push(["Trabajador", workerName]);
      rows.push(["Rol", summary.user?.role || ""]);
      rows.push(["Periodo", period]);
      rows.push(["Rango", rangeLabel]);
      rows.push(["Generado", now]);
      rows.push([]);
      rows.push(["Totales"]);
      rows.push(["Horas trabajadas", worked.toFixed(2)]);
      rows.push(["Horas contrato", (summary.contract_hours ?? 0).toFixed(2)]);
      rows.push(["Diferencia", diff.toFixed(2)]);
      rows.push(["Precio hora (€)", (summary.hourly_rate ?? 0).toFixed(2)]);
      rows.push(["Total a pagar (€)", pay.toFixed(2)]);
      if ((summary.break_hours ?? 0) > 0) {
        rows.push(["Tareas (horas)", (summary.break_hours ?? 0).toFixed(2)]);
      }
      rows.push([]);
      rows.push(["Detalle de fichajes"]);
      rows.push(["Inicio", "Fin", "Duración (h)", "Estado", "Notas"]);
      (summary.punches || []).forEach((p) => {
        const estado = p.auto_closed ? "Auto-cierre" : p.excluded ? "Excluido" : p.origin === "incidence" ? "Incidencia" : "Normal";
        rows.push([
          p.start_time ? new Date(p.start_time).toLocaleString("es-ES") : "",
          p.end_time ? new Date(p.end_time).toLocaleString("es-ES") : "EN CURSO",
          (p.duration_hours ?? 0).toFixed(2),
          estado,
          p.notes || "",
        ]);
      });
      // Breaks: apply the same filters as the on-screen timeline so the CSV
      // reflects what the admin is looking at (family filter + informativos toggle).
      const filteredBreaks = (summary.breaks || []).filter((b) => {
        if (!b.discounts && !showInfoBreaks) return false;
        if (familyFilter && b.family_id !== familyFilter) return false;
        return true;
      });
      if (filteredBreaks.length > 0) {
        rows.push([]);
        const filterNote = familyFilter
          ? ` (familia: ${breakFamilies.find((f) => f.id === familyFilter)?.name || "—"})`
          : "";
        rows.push([`Tareas${filterNote}`]);
        rows.push(["Inicio", "Fin", "Motivo", "Familia", "Detalle", "Duración (h)", "Descuenta"]);
        filteredBreaks.forEach((b) => rows.push([
          b.start_time ? new Date(b.start_time).toLocaleString("es-ES") : "",
          b.end_time ? new Date(b.end_time).toLocaleString("es-ES") : "EN CURSO",
          b.reason || "",
          b.family_name || "",
          b.custom_reason || "",
          (b.duration_hours ?? 0).toFixed(2),
          b.discounts ? "sí" : "no",
        ]));
      }
    }

    const rangeSegment = safeFileSegment(rangeLabel).replace(/_a_/, "_");
    const filename = `informe_${safeFileSegment(subjectLabel) || "sin_nombre"}_${rangeSegment || "sin_fecha"}.csv`;
    downloadCsv(filename, csvJoin(rows));
    toast.success("CSV descargado");
  };

  const stats = useMemo(() => {
    if (!summary) return [];
    if (isBreak) return [];
    if (isGroup) {
      const worked = summary.worked_hours;
      const diff = summary.difference_hours;
      const pay = summary.total_pay;
      return [
        { label: "Horas trabajadas (grupo)", value: worked.toFixed(2) + " h", icon: <Clock size={20} weight="duotone" />, testId: "summary-worked-hours", accent: "var(--primary)" },
        { label: "Horas de contrato (grupo)", value: summary.contract_hours.toFixed(2) + " h", icon: <Calendar size={20} weight="duotone" />, testId: "summary-contract-hours", accent: "#718096" },
        { label: "Diferencia (grupo)", value: (diff >= 0 ? "+" : "") + diff.toFixed(2) + " h", icon: diff >= 0 ? <TrendUp size={20} weight="duotone" /> : <TrendDown size={20} weight="duotone" />, testId: "summary-difference", accent: diff >= 0 ? "var(--primary)" : "var(--danger)" },
        { label: "Total a pagar (Σ diferencia × €/h)", value: pay.toFixed(2) + " €", icon: <CurrencyEur size={20} weight="duotone" />, testId: "summary-total-pay", accent: "var(--danger)" },
      ];
    }
    const worked = showRawTotals ? (summary.worked_hours_raw ?? summary.worked_hours) : summary.worked_hours;
    const diff = worked - summary.contract_hours;
    const pay = diff * summary.hourly_rate;
    return [
      {
        label: showRawTotals ? "Horas trabajadas (sin modif.)" : "Horas trabajadas",
        value: worked.toFixed(2) + " h",
        icon: <Clock size={20} weight="duotone" />,
        testId: "summary-worked-hours",
        accent: "var(--primary)",
      },
      {
        label: "Horas de contrato",
        value: summary.contract_hours.toFixed(2) + " h",
        icon: <Calendar size={20} weight="duotone" />,
        testId: "summary-contract-hours",
        accent: "#718096",
      },
      {
        label: "Diferencia",
        value: (diff >= 0 ? "+" : "") + diff.toFixed(2) + " h",
        icon: diff >= 0 ? <TrendUp size={20} weight="duotone" /> : <TrendDown size={20} weight="duotone" />,
        testId: "summary-difference",
        accent: diff >= 0 ? "var(--primary)" : "var(--danger)",
      },
      {
        label: "Total a pagar (diferencia × €/h)",
        value: pay.toFixed(2) + " €",
        icon: <CurrencyEur size={20} weight="duotone" />,
        testId: "summary-total-pay",
        accent: "var(--danger)",
      },
      {
        label: summary.break_hours > 0 ? "Tareas" : "Tareas (sin registros)",
        value: (summary.break_hours ?? 0).toFixed(2) + " h",
        icon: <Coffee size={20} weight="duotone" />,
        testId: "summary-break-hours",
        accent: "#d97706",
        breakdown: summary.break_breakdown,
      },
    ];
  }, [summary, showRawTotals, isGroup, isBreak]);

  return (
    <div>
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <div className="label-cap">informe</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Horas trabajadas</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={exportCsv}
            disabled={!summary}
            data-testid="export-csv-btn"
            className="no-print btn-primary px-5 py-3 rounded-xl font-display font-bold flex items-center gap-2 disabled:opacity-50"
          >
            <FileArrowDown size={18} weight="duotone" />
            Exportar CSV
          </button>
          <button
            onClick={() => window.print()}
            disabled={!summary}
            data-testid="print-report-btn"
            className="no-print px-5 py-3 rounded-xl border border-[var(--border)] font-display font-bold flex items-center gap-2 disabled:opacity-50 hover:bg-[var(--bg)]"
          >
            <Printer size={18} weight="duotone" />
            Imprimir
          </button>
        </div>
      </div>

      {pendingAdvances && (pendingAdvances.workers_count > 0 || advancesPanelOpen) && (
        <div
          className={`no-print mb-6 card-flat overflow-hidden border-l-4 ${
            pendingAdvances.workers_count > 0 ? "border-l-amber-500" : "border-l-emerald-500"
          }`}
          data-testid="pending-advances-card"
        >
          <button
            onClick={() => setAdvancesPanelOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-4 px-5 py-4 hover:bg-[var(--bg)] transition"
            data-testid="pending-advances-toggle"
          >
            <div className="flex items-center gap-3">
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center"
                style={{ background: pendingAdvances.workers_count > 0 ? "rgba(245, 158, 11, 0.15)" : "rgba(16, 185, 129, 0.15)" }}
              >
                <CurrencyEur
                  size={22}
                  weight="duotone"
                  className={pendingAdvances.workers_count > 0 ? "text-amber-600" : "text-emerald-600"}
                />
              </div>
              <div className="text-left">
                <div className="label-cap">adelantos pendientes</div>
                {pendingAdvances.workers_count > 0 ? (
                  <div className="font-display font-bold text-lg" data-testid="pending-advances-headline">
                    <span className="text-amber-700">{pendingAdvances.total_pending.toFixed(2)} €</span>
                    <span className="text-[var(--text-secondary)] font-normal"> pendientes en </span>
                    <span className="text-[var(--foreground)]">{pendingAdvances.workers_count}</span>
                    <span className="text-[var(--text-secondary)] font-normal"> trabajador{pendingAdvances.workers_count === 1 ? "" : "es"}</span>
                  </div>
                ) : (
                  <div className="font-display font-bold text-lg text-emerald-700" data-testid="pending-advances-empty">
                    Todos los adelantos están saldados
                  </div>
                )}
                {pendingAdvances.discount_pending > 0 && pendingAdvances.discount_pending !== pendingAdvances.total_pending && (
                  <div className="text-xs text-[var(--text-secondary)] mt-0.5">
                    {pendingAdvances.discount_pending.toFixed(2)} € marcados para descuento
                  </div>
                )}
              </div>
            </div>
            {pendingAdvances.workers_count > 0 && (
              advancesPanelOpen
                ? <CaretDown size={18} weight="bold" className="text-[var(--text-secondary)]" />
                : <CaretRight size={18} weight="bold" className="text-[var(--text-secondary)]" />
            )}
          </button>

          {advancesPanelOpen && pendingAdvances.workers_count > 0 && (
            <div className="border-t border-[var(--border)] bg-[var(--bg)]" data-testid="pending-advances-list">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--border)]">
                    <th className="text-left p-3"><SortHeader sortKey="name" currentKey={advSort.sortKey} currentDir={advSort.sortDir} onClick={advSort.toggleSort}>Trabajador</SortHeader></th>
                    <th className="text-center p-3"><SortHeader align="center" sortKey="role" currentKey={advSort.sortKey} currentDir={advSort.sortDir} onClick={advSort.toggleSort}>Rol</SortHeader></th>
                    <th className="text-right p-3"><SortHeader align="right" sortKey="advances_count" currentKey={advSort.sortKey} currentDir={advSort.sortDir} onClick={advSort.toggleSort}>Adelantos</SortHeader></th>
                    <th className="text-right p-3"><SortHeader align="right" sortKey="pending" currentKey={advSort.sortKey} currentDir={advSort.sortDir} onClick={advSort.toggleSort}>Pendiente</SortHeader></th>
                    <th className="text-right p-3"><SortHeader align="right" sortKey="discount_pending" currentKey={advSort.sortKey} currentDir={advSort.sortDir} onClick={advSort.toggleSort}>Descuenta</SortHeader></th>
                    <th className="text-right p-3 label-cap">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {advSort.sortedRows.map((row) => (
                    <tr
                      key={row.user_id}
                      className={`border-b border-[var(--border)] last:border-0 ${row.active ? "" : "opacity-60"}`}
                      data-testid={`pending-advance-row-${row.user_id}`}
                    >
                      <td className="p-3 font-display font-semibold">{row.name}</td>
                      <td className="p-3 text-center">
                        <span className="px-2 py-0.5 rounded-full text-xs font-display font-semibold bg-white border border-[var(--border)]">
                          {row.role}
                        </span>
                      </td>
                      <td className="p-3 text-right font-mono">{row.advances_count}</td>
                      <td className="p-3 text-right font-mono font-bold text-amber-700">{row.pending.toFixed(2)} €</td>
                      <td className="p-3 text-right font-mono text-[var(--text-secondary)]">
                        {row.discount_pending > 0 ? `${row.discount_pending.toFixed(2)} €` : "—"}
                      </td>
                      <td className="p-3 text-right">
                        <button
                          onClick={() => setAdvancesWorker({ id: row.user_id, name: row.name })}
                          data-testid={`pending-advance-open-${row.user_id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--border)] bg-white hover:bg-[var(--primary)] hover:text-white hover:border-[var(--primary)] font-display text-xs font-semibold transition"
                        >
                          Abrir
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="card-flat p-4">
          <label className="label-cap block mb-2">Trabajador</label>
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            data-testid="admin-worker-select"
            className="w-full px-3 py-2 rounded-lg border border-[var(--border)] bg-white font-display"
          >
            {workers.length === 0 && <option>Sin trabajadores</option>}
            <optgroup label="Grupos">
              {roles.map((r) => (
                <option key={r.id} value={`__group_${r.id}__`} data-testid={`group-option-${r.id}`}>{r.name} — Todos</option>
              ))}
            </optgroup>
            <optgroup label="Trabajadores">
              {workers.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name} — {roleLabel(roles, w.role)}
                </option>
              ))}
            </optgroup>
            <optgroup label="Tareas">
              <option value="__break_" data-testid="break-option-all">Tareas — Todos los motivos</option>
              {breakReasons.map((r) => (
                <option key={r} value={`__break_${r}`} data-testid={`break-option-${r}`}>Tareas — {r}</option>
              ))}
            </optgroup>
          </select>
        </div>
        <div className="card-flat p-4">
          <label className="label-cap block mb-2">Período</label>
          <div className="grid grid-cols-4 gap-2">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                data-testid={`period-${p.value}`}
                className={`py-2 rounded-lg font-display font-semibold border transition text-sm ${
                  period === p.value
                    ? "bg-[var(--primary)] text-white border-[var(--primary)]"
                    : "bg-white border-[var(--border)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div className="card-flat p-4">
          {period === "custom" ? (
            <div>
              <label className="label-cap block mb-2">Rango de fechas</label>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={dateFrom}
                  max={dateTo}
                  onChange={(e) => setDateFrom(e.target.value)}
                  data-testid="date-from-input"
                  className="flex-1 px-3 py-2 rounded-lg border border-[var(--border)] bg-white font-display text-sm"
                />
                <span className="text-[var(--text-secondary)]">→</span>
                <input
                  type="date"
                  value={dateTo}
                  min={dateFrom}
                  onChange={(e) => setDateTo(e.target.value)}
                  data-testid="date-to-input"
                  className="flex-1 px-3 py-2 rounded-lg border border-[var(--border)] bg-white font-display text-sm"
                />
              </div>
            </div>
          ) : (
            <div>
              <label className="label-cap block mb-2">Fecha de referencia</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="date-input"
                className="w-full px-3 py-2 rounded-lg border border-[var(--border)] bg-white font-display"
              />
            </div>
          )}
        </div>
      </div>

      {loading && <div className="text-center py-12 text-[var(--text-secondary)]">Calculando…</div>}

      {summary && !loading && (
        <>
          {!isBreak && (
            <div className="no-print grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {stats.map((s) => (
              <div key={s.label} className="card-flat p-6" data-testid={s.testId}>
                <div className="flex items-center justify-between mb-3">
                  <span className="label-cap">{s.label}</span>
                  <span style={{ color: s.accent }}>{s.icon}</span>
                </div>
                <div className="font-display text-3xl font-bold tracking-tight" style={{ color: s.accent }}>
                  {s.value}
                </div>
                {s.breakdown && s.breakdown.length > 0 && (
                  <div className="mt-2 space-y-0.5" data-testid={`${s.testId}-breakdown`}>
                    {s.breakdown.map((b) => (
                      <div key={b.reason} className="text-[11px] text-[var(--text-secondary)] flex justify-between gap-2">
                        <span className="truncate">
                          {b.reason}
                          {b.discounts && <span className="ml-1 text-amber-700 font-semibold">↓</span>}
                        </span>
                        <span className="font-mono">{b.hours.toFixed(2)} h</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          )}

          {isBreak && (
            <div className="card-flat overflow-hidden mb-6" data-testid="breaks-report">
              <div className="p-6 border-b border-amber-200 bg-amber-50 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="label-cap text-amber-800">informe de tareas</div>
                  <h2 className="font-display text-xl font-bold tracking-tight">
                    {breakReason ? `Motivo: ${breakReason}` : "Todos los motivos"} · <span className="text-amber-700">{(summary.total_hours || 0).toFixed(2)} h</span> · <span className="text-[var(--text-secondary)] text-base">{summary.total_count || 0} tarea{summary.total_count === 1 ? "" : "s"}</span>
                  </h2>
                </div>
                <Coffee size={22} weight="duotone" className="text-amber-600" />
              </div>

              {(summary.by_user || []).length > 0 && (
                <div className="p-6 border-b border-[var(--border)]">
                  <div className="label-cap mb-2">por trabajador</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {summary.by_user.map((u) => (
                      <div key={u.user.id} data-testid={`break-by-user-${u.user.id}`} className="flex items-center justify-between px-3 py-2 rounded-xl bg-[var(--bg)]">
                        <span className="font-display font-semibold text-sm">{u.user.name} <span className="text-[var(--text-secondary)] text-xs">— {u.user.role}</span></span>
                        <span className="font-mono text-sm">{u.hours.toFixed(2)} h · <span className="text-[var(--text-secondary)]">{u.count}</span></span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(summary.by_reason || []).length > 1 && !breakReason && (
                <div className="p-6 border-b border-[var(--border)]">
                  <div className="label-cap mb-2">por motivo</div>
                  <div className="flex flex-wrap gap-2">
                    {summary.by_reason.map((r) => (
                      <span key={r.reason} className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-300 font-display font-semibold">
                        {r.reason}
                        <span className="font-mono ml-1">{r.hours.toFixed(2)} h</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <table className="w-full">
                <thead>
                  <tr className="border-b border-[var(--border)] bg-amber-50">
                    <th className="text-left p-4"><SortHeader sortKey="user_name" currentKey={breakSumSort.sortKey} currentDir={breakSumSort.sortDir} onClick={breakSumSort.toggleSort}>Trabajador</SortHeader></th>
                    <th className="text-left p-4"><SortHeader sortKey="reason" currentKey={breakSumSort.sortKey} currentDir={breakSumSort.sortDir} onClick={breakSumSort.toggleSort}>Motivo</SortHeader></th>
                    <th className="text-left p-4"><SortHeader sortKey="start_time" currentKey={breakSumSort.sortKey} currentDir={breakSumSort.sortDir} onClick={breakSumSort.toggleSort}>Inicio</SortHeader></th>
                    <th className="text-left p-4"><SortHeader sortKey="end_time" currentKey={breakSumSort.sortKey} currentDir={breakSumSort.sortDir} onClick={breakSumSort.toggleSort}>Fin</SortHeader></th>
                    <th className="text-right p-4"><SortHeader align="right" sortKey="duration_hours" currentKey={breakSumSort.sortKey} currentDir={breakSumSort.sortDir} onClick={breakSumSort.toggleSort}>Duración</SortHeader></th>
                  </tr>
                </thead>
                <tbody>
                  {(summary.rows || []).length === 0 && (
                    <tr><td colSpan={5} className="p-8 text-center text-[var(--text-secondary)]">Sin tareas en este período.</td></tr>
                  )}
                  {breakSumSort.sortedRows.map((b) => (
                    <tr key={b.id} data-testid={`break-row-${b.id}`} className="border-b border-amber-100 last:border-0">
                      <td className="p-4 font-display font-semibold text-sm">{b.user_name}</td>
                      <td className="p-4 text-sm">{b.reason}{b.custom_reason ? ` — ${b.custom_reason}` : ""}</td>
                      <td className="p-4 font-mono text-sm">{new Date(b.start_time).toLocaleString("es-ES")}</td>
                      <td className="p-4 font-mono text-sm">{b.end_time ? new Date(b.end_time).toLocaleString("es-ES") : <span className="text-[var(--primary)] font-semibold">EN CURSO</span>}</td>
                      <td className="p-4 text-right font-mono font-semibold">{b.duration_hours.toFixed(2)} h</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Monthly extra */}
          {!isBreak && (
          <div className="no-print card-flat p-6 mb-6">
            <div className="label-cap mb-3">resumen mensual (mes actual)</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Mini label="Horas trabajadas" value={summary.month_summary.worked_hours.toFixed(2) + " h"} />
              <Mini label="Horas contrato" value={summary.month_summary.contract_hours.toFixed(2) + " h"} />
              <Mini
                label="Diferencia"
                value={(summary.month_summary.difference_hours >= 0 ? "+" : "") + summary.month_summary.difference_hours.toFixed(2) + " h"}
                color={summary.month_summary.difference_hours >= 0 ? "var(--primary)" : "var(--danger)"}
              />
              <Mini label="Total" value={summary.month_summary.total_pay.toFixed(2) + " €"} color="var(--danger)" />
            </div>
          </div>
          )}

          {isGroup && (
            <div className="card-flat overflow-hidden" data-testid="group-breakdown-table">
              <div className="p-6 border-b border-[var(--border)] flex items-center justify-between">
                <div>
                  <div className="label-cap">desglose por trabajador</div>
                  <h2 className="font-display text-xl font-bold tracking-tight">
                    {groupLabel} · {period === "custom" ? `del ${dateFrom} al ${dateTo}` : `período: ${PERIODS.find((p) => p.value === period)?.label}`}
                  </h2>
                </div>
                <div className="text-sm text-[var(--text-secondary)] font-mono">
                  {summary.breakdown?.length || 0} trabajador(es)
                </div>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[var(--border)] bg-[var(--bg)]">
                    <th className="text-left p-4"><SortHeader sortKey="user.name" currentKey={groupSort.sortKey} currentDir={groupSort.sortDir} onClick={groupSort.toggleSort}>Trabajador</SortHeader></th>
                    <th className="text-right p-4"><SortHeader align="right" sortKey="worked_hours" currentKey={groupSort.sortKey} currentDir={groupSort.sortDir} onClick={groupSort.toggleSort}>H. trabajadas</SortHeader></th>
                    <th className="text-right p-4"><SortHeader align="right" sortKey="contract_hours" currentKey={groupSort.sortKey} currentDir={groupSort.sortDir} onClick={groupSort.toggleSort}>H. contrato</SortHeader></th>
                    <th className="text-right p-4"><SortHeader align="right" sortKey="difference_hours" currentKey={groupSort.sortKey} currentDir={groupSort.sortDir} onClick={groupSort.toggleSort}>Diferencia</SortHeader></th>
                    <th className="text-right p-4"><SortHeader align="right" sortKey="hourly_rate" currentKey={groupSort.sortKey} currentDir={groupSort.sortDir} onClick={groupSort.toggleSort}>€/h</SortHeader></th>
                    <th className="text-right p-4"><SortHeader align="right" sortKey="total_pay" currentKey={groupSort.sortKey} currentDir={groupSort.sortDir} onClick={groupSort.toggleSort}>Total</SortHeader></th>
                  </tr>
                </thead>
                <tbody>
                  {(!summary.breakdown || summary.breakdown.length === 0) && (
                    <tr><td colSpan={6} className="p-8 text-center text-[var(--text-secondary)]">Sin trabajadores en este grupo.</td></tr>
                  )}
                  {groupSort.sortedRows.map((b) => (
                    <tr key={b.user.id} data-testid={`group-row-${b.user.id}`} className="border-b border-[var(--border)] last:border-0">
                      <td className="p-4 font-display font-semibold">{b.user.name}</td>
                      <td className="p-4 text-right font-mono">{b.worked_hours.toFixed(2)} h</td>
                      <td className="p-4 text-right font-mono text-[var(--text-secondary)]">{b.contract_hours.toFixed(2)} h</td>
                      <td className="p-4 text-right font-mono" style={{ color: b.difference_hours >= 0 ? "var(--primary)" : "var(--danger)" }}>
                        {(b.difference_hours >= 0 ? "+" : "") + b.difference_hours.toFixed(2)} h
                      </td>
                      <td className="p-4 text-right font-mono text-[var(--text-secondary)]">{b.hourly_rate.toFixed(2)} €</td>
                      <td className="p-4 text-right font-mono font-bold" style={{ color: "var(--danger)" }}>{b.total_pay.toFixed(2)} €</td>
                    </tr>
                  ))}
                </tbody>
                {summary.breakdown && summary.breakdown.length > 0 && (
                  <tfoot>
                    <tr className="bg-[var(--bg)] border-t-2 border-[var(--border)]">
                      <td className="p-4 font-display font-bold">TOTAL {roleLabel(roles, groupRole).toUpperCase()}</td>
                      <td className="p-4 text-right font-mono font-bold">{summary.worked_hours.toFixed(2)} h</td>
                      <td className="p-4 text-right font-mono font-bold">{summary.contract_hours.toFixed(2)} h</td>
                      <td className="p-4 text-right font-mono font-bold" style={{ color: summary.difference_hours >= 0 ? "var(--primary)" : "var(--danger)" }}>
                        {(summary.difference_hours >= 0 ? "+" : "") + summary.difference_hours.toFixed(2)} h
                      </td>
                      <td className="p-4"></td>
                      <td className="p-4 text-right font-mono font-bold" style={{ color: "var(--danger)" }}>{summary.total_pay.toFixed(2)} €</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}

          {!isGroup && !isBreak && (
          <>
          {/* Compact header shown ONLY when printing to save paper. */}
          <div className="print-only hidden mb-3" data-testid="print-compact-header">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", borderBottom: "1px solid #000", paddingBottom: "6px" }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: "14pt" }}>{summary.user?.name}</div>
                <div style={{ fontSize: "10pt", color: "#555" }}>
                  Período: {period === "custom" ? `del ${dateFrom} al ${dateTo}` : (PERIODS.find((p) => p.value === period)?.label || period)}
                </div>
              </div>
              <div style={{ textAlign: "right", fontSize: "10pt" }}>
                <div>Trabajadas: <b>{(showRawTotals ? (summary.worked_hours_raw ?? summary.worked_hours) : summary.worked_hours).toFixed(2)} h</b> · Contrato: <b>{summary.contract_hours.toFixed(2)} h</b></div>
                <div>Diferencia: <b style={{ color: ((showRawTotals ? (summary.worked_hours_raw ?? summary.worked_hours) : summary.worked_hours) - summary.contract_hours) >= 0 ? "#16a34a" : "#dc2626" }}>{((showRawTotals ? (summary.worked_hours_raw ?? summary.worked_hours) : summary.worked_hours) - summary.contract_hours).toFixed(2)} h</b> · Total: <b>{(((showRawTotals ? (summary.worked_hours_raw ?? summary.worked_hours) : summary.worked_hours) - summary.contract_hours) * (summary.hourly_rate || 0)).toFixed(2)} €</b></div>
                {(summary.break_hours ?? 0) > 0 && (
                  <div>Tareas: <b>{(summary.break_hours || 0).toFixed(2)} h</b></div>
                )}
              </div>
            </div>
          </div>
          <div className="card-flat overflow-hidden" data-testid="punch-history-table">
            <div className="p-6 border-b border-[var(--border)] flex items-center justify-between">
              <div>
                <div className="label-cap">historial de fichajes</div>
                <h2 className="font-display text-xl font-bold tracking-tight">
                  {summary.user.name} · {period === "custom" ? `del ${dateFrom} al ${dateTo}` : `período: ${PERIODS.find((p) => p.value === period)?.label}`}
                </h2>
              </div>
              <div className="flex items-center gap-3">
                <label className="no-print flex items-center gap-2 text-xs text-[var(--text-secondary)]">
                  <input
                    type="checkbox"
                    checked={showModifications}
                    onChange={(e) => setShowModifications(e.target.checked)}
                    data-testid="toggle-show-modifications"
                  />
                  Ver incidencias
                </label>
                <label className="no-print flex items-center gap-2 text-xs text-[var(--text-secondary)]">
                  <input
                    type="checkbox"
                    checked={showInfoBreaks}
                    onChange={(e) => setShowInfoBreaks(e.target.checked)}
                    data-testid="toggle-show-info-breaks"
                  />
                  Ver tareas informativas
                </label>
                {breakFamilies.length > 0 && (
                  <label className="no-print flex items-center gap-2 text-xs text-[var(--text-secondary)]">
                    <span>Familia:</span>
                    <select
                      value={familyFilter}
                      onChange={(e) => setFamilyFilter(e.target.value)}
                      data-testid="break-family-filter"
                      className="px-2 py-1 rounded-lg border border-[var(--border)] bg-white text-xs font-display font-semibold"
                    >
                      <option value="">Todas</option>
                      {breakFamilies.map((f) => (
                        <option key={f.id} value={f.id} data-testid={`break-family-filter-opt-${f.id}`}>{f.name}</option>
                      ))}
                    </select>
                  </label>
                )}
                <label className="no-print flex items-center gap-2 text-xs text-[var(--text-secondary)]">
                  <input
                    type="checkbox"
                    checked={showRawTotals}
                    onChange={(e) => setShowRawTotals(e.target.checked)}
                    data-testid="toggle-raw-totals"
                  />
                  Total sin modificaciones
                </label>
                <button
                  onClick={() => setEditPunch({ user_id: selectedId, start_time: new Date().toISOString(), end_time: "" })}
                  data-testid="add-punch-btn"
                  className={`no-print inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[var(--border)] hover:bg-white text-sm font-display font-semibold ${hidePunchActions ? "hidden" : ""}`}
                >
                  <Plus size={14} weight="bold" /> Añadir fichaje
                </button>
                <div className="text-sm text-[var(--text-secondary)] font-mono">
                  {summary.punches.length} fichaje(s)
                  {summary.incidences?.length ? ` · ${summary.incidences.length} incid.` : ""}
                </div>
              </div>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-[var(--border)] bg-[var(--bg)]">
                  <th className="text-left p-4"><SortHeader sortKey="ts" currentKey={punchSort.sortKey} currentDir={punchSort.sortDir} onClick={punchSort.toggleSort}>Entrada</SortHeader></th>
                  <th className="text-left p-4"><SortHeader sortKey="end_ts" currentKey={punchSort.sortKey} currentDir={punchSort.sortDir} onClick={punchSort.toggleSort}>Salida</SortHeader></th>
                  <th className="text-right p-4"><SortHeader align="right" sortKey="hours" currentKey={punchSort.sortKey} currentDir={punchSort.sortDir} onClick={punchSort.toggleSort}>Horas</SortHeader></th>
                  <th className="text-right p-4"><SortHeader align="right" sortKey="type" currentKey={punchSort.sortKey} currentDir={punchSort.sortDir} onClick={punchSort.toggleSort}>Estado</SortHeader></th>
                  <th className="text-right p-4 label-cap no-print">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {summary.punches.length === 0 && !summary.incidences?.length && (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-[var(--text-secondary)]">
                      Sin fichajes en este período.
                    </td>
                  </tr>
                )}
                {punchSort.sortedRows.map((row) => {
                  if (row.type === "break") {
                    const b = row.data;
                    const color = b.family_color || "#94a3b8";
                    const fmt = (iso) => iso ? new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
                    return (
                      <tr
                        key={`brk-${b.id}`}
                        data-testid={`break-inline-${b.id}`}
                        className="border-b border-[var(--border)] last:border-0"
                        style={{ background: color + "18" }}
                      >
                        <td className="p-4" style={{ borderLeft: `3px solid ${color}` }}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Coffee size={16} weight="duotone" style={{ color }} className="shrink-0" />
                            <span
                              className="text-[10px] font-display font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                              style={{ background: color + "33", color: color }}
                            >
                              {b.family_name || "tarea"}
                            </span>
                            <span className="font-display font-semibold text-sm">
                              {b.reason}{b.custom_reason ? ` — ${b.custom_reason}` : ""}
                            </span>
                            {b.discounts && (
                              <span className="text-[10px] font-display font-bold uppercase tracking-wider bg-amber-500/15 text-amber-700 border border-amber-500/40 px-1.5 py-0.5 rounded">
                                descuenta
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-[var(--text-secondary)] font-mono mt-1">
                            {fmt(b.start_time)}
                          </div>
                        </td>
                        <td className="p-4 font-mono text-sm">
                          {b.is_open ? <span className="text-[var(--primary)] font-display font-bold">EN CURSO</span> : fmt(b.end_time)}
                        </td>
                        <td className="p-4 text-right font-mono text-sm font-semibold">
                          {b.duration_hours.toFixed(2)} h
                        </td>
                        <td className="p-4 text-right">
                          <span className="text-[10px] font-display font-bold uppercase tracking-wider text-[var(--text-secondary)]">
                            Tarea
                          </span>
                        </td>
                        <td className="p-4 no-print" />
                      </tr>
                    );
                  }
                  if (row.type === "incidence") {
                    const inc = row.data;
                    const isTarea = (inc.kind || "fichaje") === "tarea";
                    const parts = [];
                    if (inc.change_entry) parts.push(`${isTarea ? "Inicio" : "Entrada"} → ${inc.entry_time}`);
                    if (inc.change_exit) parts.push(`${isTarea ? "Fin" : "Salida"} → ${inc.exit_time}`);
                    const statusStyle = inc.status === "pendiente"
                      ? "bg-yellow-100 text-yellow-700 border-yellow-300"
                      : inc.status === "aprobado"
                      ? "bg-emerald-100 text-emerald-700 border-emerald-300"
                      : "bg-red-100 text-red-700 border-red-300";
                    const kindStyle = isTarea
                      ? "bg-indigo-100 text-indigo-700 border-indigo-300"
                      : "bg-slate-100 text-slate-700 border-slate-300";
                    return (
                      <tr
                        key={`inc-${inc.id}`}
                        data-testid={`incidence-row-${inc.id}`}
                        className="border-b border-[var(--border)] last:border-0 bg-yellow-50/40"
                      >
                        <td colSpan={4} className="p-4">
                          <div className="flex flex-wrap items-center gap-3">
                            <WarningCircle size={18} weight="duotone" className="text-[var(--primary)] shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm flex items-center gap-2 flex-wrap">
                                <span className="font-display font-semibold">Incidencia</span>
                                <span
                                  className={`text-[10px] px-1.5 py-0.5 rounded border uppercase tracking-wider font-display font-bold ${kindStyle}`}
                                  data-testid={`incidence-kind-badge-${inc.id}`}
                                >
                                  {isTarea ? "Tarea" : "Fichaje"}
                                </span>
                                <span className="text-[var(--text-secondary)]">
                                  · {inc.date}
                                  {isTarea && inc.break_reason ? ` · ${inc.break_reason}` : ""}
                                  {parts.length ? ` · ${parts.join(" · ")}` : ""}
                                </span>
                              </div>
                              {inc.motivo && (
                                <div className="text-xs text-[var(--text-secondary)] italic mt-0.5">
                                  “{inc.motivo}”
                                </div>
                              )}
                              {inc.status === "denegado" && inc.motivo_rechazo && (
                                <div className="text-xs text-red-700 mt-0.5">
                                  <span className="font-semibold">Rechazada:</span> “{inc.motivo_rechazo}”
                                </div>
                              )}
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded-full border capitalize ${statusStyle}`}>
                              {inc.status}
                            </span>
                          </div>
                        </td>
                        <td className="p-4 no-print">
                          <div className="flex justify-end gap-1">
                            {inc.status === "pendiente" ? (
                              <>
                                <button
                                  onClick={() => approveIncidence(inc)}
                                  data-testid={`approve-inc-${inc.id}`}
                                  className="p-2 rounded-lg border border-emerald-300 text-emerald-700 hover:bg-emerald-500 hover:text-white"
                                  title="Aceptar"
                                >
                                  <Check size={14} weight="bold" />
                                </button>
                                <button
                                  onClick={() => denyIncidence(inc)}
                                  data-testid={`deny-inc-${inc.id}`}
                                  className="p-2 rounded-lg border border-red-300 text-red-700 hover:bg-red-500 hover:text-white"
                                  title="Denegar"
                                >
                                  <X size={14} weight="bold" />
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => deleteIncidence(inc)}
                                data-testid={`delete-inc-${inc.id}`}
                                className="p-2 rounded-lg border border-[var(--border)] hover:bg-[var(--danger)] hover:text-white hover:border-[var(--danger)]"
                                title="Eliminar"
                              >
                                <Trash size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  }
                  const p = row.data;
                  const isExcluded = !!p.excluded;
                  const isModified = (p.modified_by_incidence || []).length > 0 || p.origin === "incidence";
                  const rowClass = p.auto_closed
                    ? "bg-[#FDECEA] text-[var(--danger)]"
                    : isExcluded
                    ? "bg-gray-100 text-gray-500"
                    : "";
                  return (
                    <tr
                      key={p.id}
                      data-testid={`punch-row-${p.id}`}
                      className={`border-b border-[var(--border)] last:border-0 ${rowClass}`}
                    >
                      <td className="p-4 font-mono text-sm">
                        {isModified && (
                          <WarningCircle
                            size={14}
                            weight="fill"
                            className="inline text-[var(--primary)] mr-1"
                            title="Modificado por incidencia"
                          />
                        )}
                        {formatDateTime(p.start_time)}
                        {p.origin === "incidence" && (
                          <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-display font-bold uppercase tracking-wider bg-emerald-500/15 text-emerald-600 border border-emerald-500/40 px-1.5 py-0.5 rounded" title="Línea generada por una incidencia aprobada" data-testid={`incidence-approved-${p.id}`}>
                            ✓ incidencia aprobada
                          </span>
                        )}
                        {p.start_distance_m != null && (
                          <a
                            href={`https://www.google.com/maps?q=${p.start_lat},${p.start_lng}`}
                            target="_blank"
                            rel="noreferrer"
                            className="ml-2 inline-flex items-center gap-1 text-xs text-[var(--text-secondary)] hover:text-[var(--primary)]"
                            title="Ver ubicación de entrada"
                          >
                            <MapPin size={12} weight="duotone" /> {Math.round(p.start_distance_m)} m
                          </a>
                        )}
                      </td>
                      <td className="p-4 font-mono text-sm">
                        {p.end_time ? formatDateTime(p.end_time) : "—"}
                        {p.auto_closed && (
                          <span className="ml-2 inline-flex items-center gap-1 text-xs font-display font-semibold">
                            <Warning size={14} weight="fill" /> auto-cierre 01:30
                          </span>
                        )}
                        {p.end_distance_m != null && !p.auto_closed && (
                          <a
                            href={`https://www.google.com/maps?q=${p.end_lat},${p.end_lng}`}
                            target="_blank"
                            rel="noreferrer"
                            className="ml-2 inline-flex items-center gap-1 text-xs text-[var(--text-secondary)] hover:text-[var(--primary)]"
                            title="Ver ubicación de salida"
                          >
                            <MapPin size={12} weight="duotone" /> {Math.round(p.end_distance_m)} m
                          </a>
                        )}
                      </td>
                      <td className="p-4 text-right font-mono font-semibold">
                        {p.auto_closed || isExcluded ? <s>{p.duration_hours.toFixed(2)} h</s> : `${p.duration_hours.toFixed(2)} h`}
                      </td>
                      <td className="p-4 text-right">
                        <span
                          className={`text-xs px-2 py-1 rounded-full font-display font-semibold ${
                            p.auto_closed
                              ? "bg-[var(--danger)] text-white"
                              : isExcluded
                              ? "bg-gray-300 text-gray-700"
                              : p.is_open
                              ? "bg-[var(--primary)] text-white"
                              : "bg-[var(--border)] text-[var(--text-secondary)]"
                          }`}
                        >
                          {p.auto_closed ? "no computa" : isExcluded ? "descontado" : p.is_open ? "abierto" : "cerrado"}
                        </span>
                      </td>
                      <td className="p-4 no-print">
                        <div className="flex justify-end gap-1">
                          {!hidePunchActions && (
                            <>
                              <button
                                onClick={() => setEditPunch({ ...p, user_id: selectedId })}
                                data-testid={`edit-punch-${p.id}`}
                                className="p-2 rounded-lg border border-[var(--border)] hover:bg-white"
                                title="Editar"
                              >
                                <PencilSimple size={14} />
                              </button>
                              {p.is_open && (
                                <button
                                  onClick={() => forceClose(p)}
                                  data-testid={`close-punch-${p.id}`}
                                  className="p-2 rounded-lg border border-[var(--border)] hover:bg-white"
                                  title="Cerrar (desfichar)"
                                >
                                  <Clock size={14} />
                                </button>
                              )}
                              <button
                                onClick={() => deletePunch(p)}
                                data-testid={`delete-punch-${p.id}`}
                                className="p-2 rounded-lg border border-[var(--border)] hover:bg-[var(--danger)] hover:text-white hover:border-[var(--danger)]"
                                title="Eliminar fichaje"
                              >
                                <Trash size={14} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
          )}

          {!isGroup && !isBreak && summary.breaks && (
            <div className="no-print card-flat overflow-hidden" data-testid="breaks-table">
              <div className="p-6 border-b border-[var(--border)] flex items-center justify-between">
                <div>
                  <div className="label-cap">tareas</div>
                  <h2 className="font-display text-xl font-bold tracking-tight">
                    {summary.breaks.length} tarea{summary.breaks.length === 1 ? "" : "s"} · total {(summary.break_hours || 0).toFixed(2)}h
                    {summary.break_discount_time ? " · descontado del total" : " · informativo"}
                  </h2>
                </div>
                <Coffee size={22} weight="duotone" className="text-amber-600" />
              </div>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[var(--border)] bg-[var(--bg)]">
                    <th className="text-left p-4"><SortHeader sortKey="reason" currentKey={breaksSort.sortKey} currentDir={breaksSort.sortDir} onClick={breaksSort.toggleSort}>Motivo</SortHeader></th>
                    <th className="text-left p-4"><SortHeader sortKey="start_time" currentKey={breaksSort.sortKey} currentDir={breaksSort.sortDir} onClick={breaksSort.toggleSort}>Inicio</SortHeader></th>
                    <th className="text-left p-4"><SortHeader sortKey="end_time" currentKey={breaksSort.sortKey} currentDir={breaksSort.sortDir} onClick={breaksSort.toggleSort}>Fin</SortHeader></th>
                    <th className="text-right p-4"><SortHeader align="right" sortKey="duration_hours" currentKey={breaksSort.sortKey} currentDir={breaksSort.sortDir} onClick={breaksSort.toggleSort}>Duración</SortHeader></th>
                  </tr>
                </thead>
                <tbody>
                  {summary.breaks.length === 0 && (
                    <tr><td colSpan={4} className="p-8 text-center text-[var(--text-secondary)]" data-testid="breaks-table-empty">Sin tareas en este período.</td></tr>
                  )}
                  {breaksSort.sortedRows.map((b) => {
                    const color = b.family_color || "#f59e0b";
                    return (
                      <tr key={b.id} data-testid={`break-row-${b.id}`} className="border-b border-[var(--border)] last:border-0" style={{ background: color + "14" }}>
                        <td className="p-4" style={{ borderLeft: `3px solid ${color}` }}>
                          <div className="flex items-center gap-2 flex-wrap">
                            {b.family_name && (
                              <span
                                className="inline-flex items-center text-[10px] font-display font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                                style={{ background: color + "33", color: color }}
                              >
                                {b.family_name}
                              </span>
                            )}
                            <span
                              className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full text-white font-display font-semibold"
                              style={{ background: color }}
                            >
                              <Coffee size={12} weight="duotone" />
                              {b.reason}{b.custom_reason ? ` — ${b.custom_reason}` : ""}
                            </span>
                            {b.discounts && (
                              <span className="text-[10px] font-display font-bold uppercase tracking-wider bg-amber-500/15 text-amber-700 border border-amber-500/40 px-1.5 py-0.5 rounded">
                                descuenta
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-4 font-mono text-sm">{new Date(b.start_time).toLocaleString("es-ES")}</td>
                        <td className="p-4 font-mono text-sm">{b.end_time ? new Date(b.end_time).toLocaleString("es-ES") : <span className="text-[var(--primary)] font-display font-semibold">EN CURSO</span>}</td>
                        <td className="p-4 text-right font-mono font-semibold">{b.duration_hours.toFixed(2)} h</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {editPunch && (
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="card-flat w-full max-w-md p-8 relative">
                <button onClick={() => setEditPunch(null)} className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-[var(--border)] flex items-center justify-center"><X size={18} /></button>
                <h2 className="font-display text-2xl font-bold tracking-tight mb-6">{editPunch.id ? "Editar fichaje" : "Nuevo fichaje"}</h2>
                <form onSubmit={savePunch} className="space-y-4">
                  <div>
                    <label className="label-cap block mb-2">Entrada</label>
                    <input
                      type="datetime-local"
                      required
                      value={toLocalInput(editPunch.start_time)}
                      onChange={(e) => setEditPunch({ ...editPunch, start_time: new Date(e.target.value).toISOString() })}
                      className="w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-white"
                    />
                  </div>
                  <div>
                    <label className="label-cap block mb-2">Salida (opcional)</label>
                    <input
                      type="datetime-local"
                      value={toLocalInput(editPunch.end_time)}
                      onChange={(e) => setEditPunch({ ...editPunch, end_time: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                      className="w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-white"
                    />
                  </div>
                  {editPunch.id && (
                    <label className="flex items-center gap-3 p-3 rounded-xl border border-[var(--border)] cursor-pointer bg-[var(--bg)]">
                      <input
                        type="checkbox"
                        checked={!editPunch.excluded}
                        onChange={(e) => setEditPunch({ ...editPunch, excluded: !e.target.checked })}
                        data-testid="edit-punch-computes"
                      />
                      <div className="flex-1">
                        <div className="font-display font-semibold text-sm">Este fichaje computa en el total</div>
                        <div className="text-xs text-[var(--text-secondary)]">
                          Desmárcalo para dejarlo como descontado (fondo gris). Útil si una incidencia lo dejó en gris y quieres volver a activarlo.
                        </div>
                      </div>
                    </label>
                  )}
                  <div className="flex justify-end gap-3 pt-2">
                    <button type="button" onClick={() => setEditPunch(null)} className="px-5 py-3 rounded-xl border border-[var(--border)] font-display font-semibold">Cancelar</button>
                    <button type="submit" disabled={savingPunch} className="btn-primary px-6 py-3 rounded-xl font-display font-bold disabled:opacity-50">{savingPunch ? "…" : "Guardar"}</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </>
      )}

      {advancesWorker && (
        <AdvancesDialog
          worker={advancesWorker}
          onClose={() => { setAdvancesWorker(null); loadPendingAdvances(); }}
        />
      )}
    </div>
  );
}

function Mini({ label, value, color }) {
  return (
    <div>
      <div className="label-cap mb-1">{label}</div>
      <div className="font-display text-xl font-bold tracking-tight" style={{ color: color || "var(--text-primary)" }}>
        {value}
      </div>
    </div>
  );
}

// Merge punches, incidences, and breaks into a chronological list (most recent first).
// Incidences use their `date` (YYYY-MM-DD) as time anchor at 23:59 so they
// appear near the top of their day but AFTER any punch of the same day.
// Breaks: discounting breaks are always included; non-discounting ("informativos")
// only when includeInfoBreaks=true. If familyFilter is set, only breaks whose
// family_id matches are included. Colspan(=5) sub-rows in the table.
function mergeRows(summary, includeIncidences, includeInfoBreaks, familyFilter) {
  const rows = [];
  for (const p of summary.punches || []) {
    rows.push({ type: "punch", ts: new Date(p.start_time).getTime(), end_ts: p.end_time ? new Date(p.end_time).getTime() : null, hours: p.duration_hours, data: p });
  }
  if (includeIncidences) {
    for (const inc of summary.incidences || []) {
      const anchor = new Date(`${inc.date}T23:59:59`).getTime();
      rows.push({ type: "incidence", ts: anchor, data: inc });
    }
  }
  for (const b of summary.breaks || []) {
    if (!b.discounts && !includeInfoBreaks) continue;
    if (familyFilter && b.family_id !== familyFilter) continue;
    rows.push({ type: "break", ts: new Date(b.start_time).getTime(), end_ts: b.end_time ? new Date(b.end_time).getTime() : null, hours: b.duration_hours, data: b });
  }
  rows.sort((a, b) => b.ts - a.ts);
  return rows;
}
